import { Table } from "antd";
import moment from "moment";
import { type RouteComponentProps } from "react-router";

import { IconCaretLargeRight } from "@btnet/blueprint-icons";
import { BTText } from "@btnet/core-components/BTText";

import { PaymentStatus } from "types/enum";

import { sum } from "utilities/math/math";
import { routes } from "utilities/routes";

import { BTLinkRelative } from "commonComponents/btWrappers/BTLink/BTLink";
import { type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { BTRow } from "commonComponents/btWrappers/BTRow/BTRow";
import { BTTable, type IBTColumnProps } from "commonComponents/btWrappers/BTTable/BTTable";
import { BTTitle } from "commonComponents/btWrappers/BTTitle/BTTitle";
import { MerchantPaymentStatusTag } from "commonComponents/entity/merchantPayment/common/MerchantPaymentStatus/MerchantPaymentStatus";
import { CurrencyDisplay } from "commonComponents/financial/CurrencyDisplay/CurrencyDisplay";
import { DateDisplayDate } from "commonComponents/utilities/DateDisplay/DateDisplayDate";
import "commonComponents/utilities/LineItemContainerV2/LineItemTotal/LineItemTotal.less";
import { PageSection } from "commonComponents/utilities/PageSection/PageSection";
import { StatusTagDisplay } from "commonComponents/utilities/Status/StatusTagDisplay";

import { BillCollapsibleSection } from "entity/bill/Bill/BillCollapsibleSection/BillCollapsibleSection";
import { RouteBillPayment } from "entity/bill/BillPayment/RouteBillPayment";
import "entity/invoicePayment/InvoicePaymentHistory/PaymentHistory.less";

export interface IBillPaymentForListData {
    paymentId: number;
    addedDate: string;
    scheduledDate?: string | null;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string | null;
    status: PaymentStatus;
    /** Integer parent PK for vendor-credit settlements. Used by v3 API routes. */
    parentBillPaymentId?: number | null;
    /** True when this row is a vendor-credit application (routes by parentBillPaymentId, status shows "Applied"). */
    isVendorCreditApplied?: boolean;
}

export class BillPaymentForList {
    constructor(data: IBillPaymentForListData) {
        this.paymentId = data.paymentId;
        this.addedDate = moment(data.addedDate);
        this.scheduledDate = data.scheduledDate ? moment(data.scheduledDate) : undefined;
        this.paymentAmount = data.paymentAmount;
        this.checkNumber = data.checkNumber;
        this.paymentMethod = data.paymentMethod ?? "";
        this.status = data.status;
        this.parentBillPaymentId = data.parentBillPaymentId ?? null;
        this.isVendorCreditApplied = data.isVendorCreditApplied ?? false;
    }
    paymentId: number;
    addedDate: moment.Moment;
    scheduledDate: moment.Moment | undefined;
    paymentAmount: number;
    checkNumber: string;
    paymentMethod: string;
    status: PaymentStatus;
    parentBillPaymentId: number | null;
    isVendorCreditApplied: boolean;
}

const NON_COUNTING_STATUSES = [
    PaymentStatus.Expired,
    PaymentStatus.Failed,
    PaymentStatus.Refunded,
    PaymentStatus.Void,
];

const getPaymentTotal = (rows: readonly BillPaymentForList[]) =>
    sum(
        rows.map((row) => (NON_COUNTING_STATUSES.includes(row.status) ? 0 : row.paymentAmount)),
    ).toNumber();

const getPaymentDetailPath = (record: BillPaymentForList) =>
    record.isVendorCreditApplied && record.parentBillPaymentId
        ? routes.billPayment.getDetailByParentBillPaymentIdPath(record.parentBillPaymentId)
        : routes.billPayment.getDetailsPath(record.paymentId);

const getPaymentRowKey = (record: BillPaymentForList) =>
    record.isVendorCreditApplied && record.parentBillPaymentId
        ? `vc-${record.parentBillPaymentId}`
        : record.paymentId.toString();

const PaymentStatusTag = ({ record }: { record: BillPaymentForList }) => {
    if (record.isVendorCreditApplied) {
        return (
            <StatusTagDisplay
                statusType="success"
                statusText="Applied"
                data-testid="billPaymentStatusApplied"
            />
        );
    }

    return (
        <MerchantPaymentStatusTag
            status={record.status}
            statusReason={
                record.status === PaymentStatus.Scheduled && record.scheduledDate
                    ? `This payment is scheduled to send on ${record.scheduledDate.format(
                          "MMMM D, YYYY",
                      )}. To cancel, void the payment before the scheduled date.`
                    : undefined
            }
        />
    );
};

const PaymentCard = ({ record }: { record: BillPaymentForList }) => {
    const isScheduled = record.status === PaymentStatus.Scheduled && record.scheduledDate;

    return (
        <BTLinkRelative
            to={getPaymentDetailPath(record)}
            isUnderline={false}
            data-testid="billPaymentCard"
            className="flex items-center justify-between gap-2 rounded-md border border-default p-3 text-fg-default hover:text-fg-default"
        >
            <div className="flex min-w-0 flex-1 flex-col items-start gap-1">
                <PaymentStatusTag record={record} />
                <BTText size="normal-sm" className="truncate text-gray-70">
                    {isScheduled ? (
                        <>
                            Sends{" "}
                            <DateDisplayDate
                                value={record.scheduledDate!.toDate()}
                                data-testid="billPaymentScheduledDate"
                            />
                        </>
                    ) : (
                        <DateDisplayDate
                            value={record.addedDate.toDate()}
                            data-testid="billPaymentAddedDate"
                        />
                    )}
                    {record.paymentMethod && ` \u00b7 ${record.paymentMethod}`}
                </BTText>
            </div>
            <div className="flex shrink-0 items-center gap-2">
                <BTText size="distinct-sm" className="tabular-nums">
                    <b>
                        <CurrencyDisplay
                            value={record.paymentAmount}
                            data-testid="billPaymentAmount"
                        />
                    </b>
                </BTText>
                <span className="inline-flex text-gray-70" aria-hidden="true">
                    <IconCaretLargeRight size="md" />
                </span>
            </div>
        </BTLinkRelative>
    );
};

interface IBillPaymentHistoryProps extends RouteComponentProps {
    payments: BillPaymentForList[];
    entityName: string;
    onReload: () => Promise<void>;
    isMobileLayout?: boolean;
}

export const BillPaymentHistory: React.FunctionComponent<IBillPaymentHistoryProps> = ({
    payments,
    entityName,
    history,
    match,
    onReload,
    isMobileLayout = false,
}) => {
    const hasPayments = payments.length > 0;

    const billPaymentHistoryColumns: IBTColumnProps<BillPaymentForList>[] = [];

    const modalConfig: IModalConfiguration = {
        beforeClose: () => {
            void onReload();
            history.replace(match.url);
        },
        parentRoute: match.url,
    };

    billPaymentHistoryColumns.push({
        dataIndex: "addedDate",
        title: "Date",
        render: (paymentDate: moment.Moment, record: BillPaymentForList) =>
            record.status === PaymentStatus.Scheduled && record.scheduledDate ? (
                <span>
                    Scheduled date:{" "}
                    <DateDisplayDate
                        value={record.scheduledDate.toDate()}
                        data-testid="billPaymentScheduledDate"
                    />
                </span>
            ) : (
                <DateDisplayDate value={paymentDate.toDate()} data-testid="billPaymentAddedDate" />
            ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentAmount",
        title: "Payment Amount",
        align: "right",
        render: (paymentAmount: number) => (
            <CurrencyDisplay value={paymentAmount} data-testid="billPaymentAmount" />
        ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentMethod",
        title: "Payment method",
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "status",
        title: "Status",
        render: (_status: PaymentStatus, record: BillPaymentForList) => (
            <PaymentStatusTag record={record} />
        ),
        className: "VerticalAlignRow",
    });

    billPaymentHistoryColumns.push({
        dataIndex: "paymentId",
        render: (_paymentId: number, record: BillPaymentForList) => (
            <BTLinkRelative to={getPaymentDetailPath(record)}>View Details</BTLinkRelative>
        ),
    });

    const content = (
        <>
            {!hasPayments && (
                <span data-testid="billPaymentHistoryEmptyStateText">
                    Payments applied to this {entityName} will be listed here
                </span>
            )}
            {hasPayments && isMobileLayout && (
                <div className="flex flex-col gap-2 pt-2" data-testid="billPaymentCards">
                    {payments.map((record) => (
                        <PaymentCard key={getPaymentRowKey(record)} record={record} />
                    ))}
                    <div className="LineItemTotal">
                        <div className="LineItemTotalRow">
                            <BTText size="distinct-sm">Total:</BTText>
                            <BTText size="distinct-sm">
                                <b>
                                    <CurrencyDisplay
                                        value={getPaymentTotal(payments)}
                                        data-testid="billPaymentTotal"
                                    />
                                </b>
                            </BTText>
                        </div>
                    </div>
                </div>
            )}
            {hasPayments && !isMobileLayout && (
                <div className="BillPaymentHistoryScrollable">
                    <BTTable
                        dataSource={payments}
                        rowKey={getPaymentRowKey}
                        columns={billPaymentHistoryColumns}
                        size="small"
                        pagination={false}
                        summary={(data) => (
                            <Table.Summary>
                                <Table.Summary.Row>
                                    {billPaymentHistoryColumns.map((x, index) => {
                                        if (x.title === "Payment Amount") {
                                            return (
                                                <Table.Summary.Cell
                                                    key={index}
                                                    index={index}
                                                    className="text-right"
                                                >
                                                    <strong>
                                                        Total:{" "}
                                                        <CurrencyDisplay
                                                            value={getPaymentTotal(data)}
                                                            data-testid="billPaymentTotal"
                                                        />
                                                    </strong>
                                                </Table.Summary.Cell>
                                            );
                                        } else {
                                            return <Table.Summary.Cell key={index} index={index} />;
                                        }
                                    })}
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />
                </div>
            )}
        </>
    );

    return (
        <div data-testid="billPaymentHistory">
            <RouteBillPayment modalConfig={modalConfig} fromBill={true} />
            {isMobileLayout ? (
                <BillCollapsibleSection
                    title="Payment history"
                    testid="paymentHistoryCollapsible"
                    defaultOpen={false}
                >
                    {content}
                </BillCollapsibleSection>
            ) : (
                <PageSection>
                    <BTRow>
                        <BTTitle level={3}>Payment history</BTTitle>
                    </BTRow>
                    {content}
                </PageSection>
            )}
        </div>
    );
};
